import React from 'react';
import { useStatistics } from '../queries';
import { AreaChart } from '@tremor/react';
import { formatPrice } from '@dashboard/services/formatting';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import Card from '@dashboard/components/Card';
import { useStatisticsContext } from '../Dashboard';
import { formatTimestamp, formatValue } from '../dates';
import { isNumber } from 'lodash';
import ChartMeta from './ChartMeta';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import Spinner from '@dashboard/components/Spinner';

export default function MonthlyRecurringRevenue() {
  const { data, isLoading } = useStatistics('recurringRevenue');
  const { unit, range } = useStatisticsContext();

  const recurringRevenueData = data?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    MRR: formatValue(timestamp, value),
  }));

  const maxValue = data && Math.max(...data?.map(({ value }) => value), 0);
  const maxValueFormatted = formatPrice({
    amount: maxValue,
    currency: 'USD',
  });
  const minValue = data && Math.min(...data?.map(({ value }) => value), 0);
  const minValueFormatted = formatPrice({
    amount: minValue,
    currency: 'USD',
  });

  // last entry
  const currentRecurringRevenue = data?.[data.length - 1]?.value;

  return (
    <Card variant="none">
      <CardHeader variant="card">
        <CardHeaderTitle variant="card">
          <div>
            <div className="text-base font-normal">
              Monthly recurring revenue{' '}
              <InfoTooltip>
                Monthly recurring revenue (MRR) is your estimated monthly revenue from all active
                and overdue subscriptions.
              </InfoTooltip>
            </div>

            <div className="text-xl mt-1.5 font-normal">
              {formatPrice({
                amount: currentRecurringRevenue,
                currency: 'USD',
              })}
            </div>
          </div>
        </CardHeaderTitle>
      </CardHeader>

      <CardContent variant="root">
        {isLoading ? (
          <div className="flex items-center justify-center h-44">
            <Spinner />
          </div>
        ) : (
          <div className="relative pl-8">
            {recurringRevenueData && (
              <ChartMeta maxValue={maxValueFormatted} minValue={minValueFormatted} />
            )}

            <AreaChart
              className="mt-4 h-44"
              data={recurringRevenueData}
              categories={['MRR']}
              index="timestamp"
              colors={['amber']}
              valueFormatter={value =>
                isNumber(value)
                  ? formatPrice({
                      amount: value,
                      currency: 'USD',
                    })
                  : value
              }
              showAnimation
              allowDecimals={false}
              showLegend={false}
              showGridLines={false}
              showYAxis={false}
              showXAxis
              startEndOnly
              showGradient
              padding={{ left: 16, right: 16 }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
